import React from 'react'
import YnsIcon from '../../icons/YnsIcon'
import { useCandidateChat } from '~/components/coach_chat/candidate/CandidateChatContext'

/**
 * Rating component that displays and optionally allows setting a 5-star rating
 * @param {Object} props
 * @param {number} [props.value] - Initial rating value (0-5)
 * @param {function} [props.onChange] - Callback when rating changes
 * @param {string} [props.size] - Size of star icons: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {string} [props.className] - Additional CSS classes
 */
const Rating = ({
  value: initialValue = 0,
  onChange,
  size = 'md',
  className = '',
}) => {
  const { getDarkModeClasses } = useCandidateChat()
  const [rating, setRating] = React.useState(initialValue)
  const [hoverValue, setHoverValue] = React.useState(0)
  const isInteractive = !!onChange

  const handleMouseEnter = (index) => {
    if (isInteractive) {
      setHoverValue(index)
    }
  }

  const handleMouseLeave = () => {
    if (isInteractive) {
      setHoverValue(0)
    }
  }

  const handleClick = (index) => {
    if (isInteractive) {
      setRating(index)
      onChange?.(index)
    }
  }

  const displayValue = hoverValue || rating

  // Size mapping for star icons
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  }

  const starSizeClass = sizeClasses[size] || sizeClasses.md

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {[1, 2, 3, 4, 5].map((index) => (
        <span
          key={index}
          className={`focus:outline-none ${
            isInteractive ? 'cursor-pointer' : 'cursor-default'
          }`}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
          onClick={() => handleClick(index)}
          role={isInteractive ? 'button' : undefined}
          tabIndex={isInteractive ? 0 : undefined}
        >
          <YnsIcon
            name={index <= displayValue ? 'star-solid' : 'star'}
            className={`${starSizeClass} ${
              index <= displayValue
                ? 'text-yellow-400'
                : getDarkModeClasses('text-cod-gray-200', 'text-gray-500')
            }`}
          />
        </span>
      ))}
    </div>
  )
}

export default Rating
